/* eslint-disable @typescript-eslint/no-explicit-any */
// @ts-nocheck
/**
 * DropOffView — voters who voted in a "base year" general election but
 * skipped a "compare year" general election.
 *
 * Backed by `fetchDropOff` (mock-or-live wrapper) which calls the live
 * `GET /voters/analytics/drop-off` endpoint shipped by Agent A on
 * 2026-04-11. The backend returns AGGREGATE rollups (tenant-wide
 * totals + per-precinct breakdown) plus an OPTIONAL capped sample of
 * voter IDs — it intentionally does NOT expose the full per-voter list
 * via this endpoint, because bulk PII export needs to flow through the
 * (still-TBD) ExportEvent-logged path.
 *
 * The view lets the user pick base/compare years + election type,
 * shows the headline drop-off rate, sortable per-precinct breakdown,
 * and a CSV export of the by-precinct rollup. Drilling a precinct
 * jumps to PrecinctView.
 *
 * Contract: see `memory-bank/apiContracts-analytics-v1.md` (kept in
 * sync with `backend/app/schemas/voter.py` `DropOffResponse`).
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  downloadCsv,
  dropOffToCsv,
  fetchDropOff,
} from '@/api/analytics';
import type { DropOffPrecinctRow, DropOffResponse } from '@/types/analytics';
import { InfoTip } from '@/components/InfoTip';
import { VisualKey } from '@/components/VisualKey';
import { DeltaChip } from '@/components/DeltaChip';
import { BarMeter } from '@/components/BarMeter';
import { RankBadge } from '@/components/RankBadge';
import { OnboardingModal, OnboardingHelpButton } from '@/components/OnboardingModal';
import { ViewHeader } from '@/components/ViewHeader';
import { LiveBadge } from '@/components/LiveBadge';

// -------------------------------------------------------------------------
// Main view
// -------------------------------------------------------------------------

type SortKey = 'precinct_num' | 'base_count' | 'dropped_count' | 'drop_off_pct';

export function DropOffView({
  onBack,
  onDrill,
}: {
  onBack: () => void;
  onDrill?: (target: string, value: any) => void;
}) {
  const [baseYear, setBaseYear] = useState(2020);
  const [compareYear, setCompareYear] = useState(2022);
  const [electionType, setElectionType] = useState('General');
  const [includeSample, setIncludeSample] = useState(false);
  const [data, setData] = useState<DropOffResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('drop_off_pct');
  const [sortDesc, setSortDesc] = useState(true);
  const [forceOnboard, setForceOnboard] = useState(false);

  const usingMock = (() => {
    const raw = (import.meta as any).env?.VITE_USE_MOCK_ANALYTICS;
    if (raw === undefined || raw === null || raw === '') return true;
    return raw !== '0' && raw !== 'false';
  })();

  function load() {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchDropOff({
      baseYear,
      compareYear,
      electionType,
      includeVoterSample: includeSample,
    })
      .then((d) => {
        if (!cancelled) {
          setData(d);
          setLoading(false);
        }
      })
      .catch((e: any) => {
        if (!cancelled) {
          const status = e?.response?.status;
          if (status === 400) {
            setError(
              'Invalid year pair. The base year must be earlier than the compare year, and both must have matching elections in the database.',
            );
          } else if (status === 404) {
            setError(
              'Drop-off endpoint not found. Backend may not be running — flip VITE_USE_MOCK_ANALYTICS=1 in .env to view fixtures.',
            );
          } else if (status === 401) {
            setError('Not authenticated — log in to query drop-off data.');
          } else if (status === 403) {
            setError('Your role does not have analytics read access.');
          } else if (status) {
            setError(`Backend error ${status}`);
          } else {
            setError('Could not reach the analytics backend.');
          }
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }

  // Auto-load on mount with the defaults so users see something immediately.
  useEffect(() => {
    return load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sortedRows = useMemo<DropOffPrecinctRow[]>(() => {
    if (!data) return [];
    const rows = [...data.by_precinct];
    rows.sort((a, b) => {
      let cmp: number;
      if (sortKey === 'precinct_num')
        cmp = a.precinct_num.localeCompare(b.precinct_num);
      else if (sortKey === 'base_count') cmp = a.base_count - b.base_count;
      else if (sortKey === 'dropped_count')
        cmp = a.dropped_count - b.dropped_count;
      else cmp = a.drop_off_pct - b.drop_off_pct;
      return sortDesc ? -cmp : cmp;
    });
    return rows;
  }, [data, sortKey, sortDesc]);

  function toggleSort(k: SortKey) {
    if (k === sortKey) setSortDesc((d) => !d);
    else {
      setSortKey(k);
      setSortDesc(k !== 'precinct_num');
    }
  }

  function handleExport() {
    if (!data) return;
    const csv = dropOffToCsv(data);
    const filename = `dropoff_${data.base_year}_to_${data.compare_year}_${data.election_type}.csv`;
    downloadCsv(filename, csv);
  }

  return (
    <div className="space-y-6">
      <OnboardingModal
        viewKey="dropoffview"
        title="Drop-Off Detector"
        intro="Find voters who showed up in a base-year election but skipped a later election. This is your reactivation list — folks who already proved they vote, just need a nudge to come back."
        termIds={['drop_off', 'roster_turnout', 'precinct', 'turnout_heatmap']}
        workflows={[
          'Pick a base year your side won (e.g. 2020 General) and a compare year you under-performed.',
          'Sort precincts by drop-off % to find your biggest reactivation opportunities.',
          'Drill into any precinct to see who lives there and plan a friendly knock.',
          'Export the per-precinct breakdown as CSV to share with your field team.',
        ]}
        forceOpen={forceOnboard}
        onClose={() => setForceOnboard(false)}
      />

      <Header onBack={onBack} usingMock={usingMock} onHelp={() => setForceOnboard(true)} />
      <VisualKey />

      {/* Filter strip */}
      <div className="bg-bg-raised rounded-xl p-4 border border-border-base">
        <div className="flex items-end gap-3 flex-wrap">
          <Field label="Base year (showed up)">
            <input
              type="number"
              min={2000}
              max={2100}
              value={baseYear}
              onChange={(e) => setBaseYear(Number(e.target.value) || 2020)}
              className="bg-bg-base border border-border-base rounded px-2 py-1 text-fg-primary text-sm w-24"
            />
          </Field>
          <Field label="Compare year (skipped)">
            <input
              type="number"
              min={2000}
              max={2100}
              value={compareYear}
              onChange={(e) => setCompareYear(Number(e.target.value) || 2022)}
              className="bg-bg-base border border-border-base rounded px-2 py-1 text-fg-primary text-sm w-24"
            />
          </Field>
          <Field label="Election type">
            <select
              value={electionType}
              onChange={(e) => setElectionType(e.target.value)}
              className="bg-bg-base border border-border-base rounded px-2 py-1 text-fg-primary text-sm"
            >
              <option value="General">General</option>
              <option value="Primary">Primary</option>
              <option value="Presidential Primary">Presidential Primary</option>
              <option value="Special">Special</option>
            </select>
          </Field>
          <label className="flex items-center gap-2 text-xs text-fg-secondary select-none mb-1">
            <input
              type="checkbox"
              checked={includeSample}
              onChange={(e) => setIncludeSample(e.target.checked)}
              className="accent-red-600 min-h-11 min-w-11 sm:min-h-0 sm:min-w-0"
            />
            Include voter ID sample (max 100)
          </label>
          <button
            onClick={load}
            disabled={loading}
            className="bg-accent hover:bg-accent-hover disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-bold min-h-11 sm:min-h-0"
          >
            {loading ? 'Loading…' : 'Run Query'}
          </button>
          <button
            onClick={handleExport}
            disabled={!data || data.by_precinct.length === 0}
            className="bg-status-success-bg hover:bg-status-success-bg/80 disabled:opacity-40 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm font-bold min-h-11 sm:min-h-0"
            title={data ? `Export ${data.by_precinct.length} precincts to CSV` : 'No data to export'}
          >
            ⬇ Export CSV
          </button>
        </div>
      </div>

      {/* Error / status */}
      {error && (
        <div className="bg-status-danger-bg/30 rounded-xl p-4 border border-status-danger text-status-danger text-sm">
          {error}
        </div>
      )}

      {/* Result block */}
      {data && !error && (
        <>
          {/* Headline tiles */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat
              label={`${data.base_year} ${data.election_type}`}
              value={data.base_count.toLocaleString()}
              hint="voted in base year"
            />
            <Stat
              label={`${data.compare_year} retained`}
              value={data.retained_count.toLocaleString()}
              hint={`also voted in ${data.compare_year}`}
            />
            <Stat
              label="Dropped off"
              value={data.dropped_count.toLocaleString()}
              hint={`skipped ${data.compare_year}`}
              accent
            />
            <Stat
              label="Drop-off rate"
              value={`${data.drop_off_pct.toFixed(1)}%`}
              hint="of base-year voters"
              accent
            />
          </div>

          {/* Per-precinct breakdown */}
          <div className="bg-bg-raised rounded-xl p-6 border border-border-base">
            <div className="flex items-baseline justify-between flex-wrap gap-2 mb-4">
              <div>
                <h2 className="text-lg font-bold text-fg-primary flex items-center gap-2">
                  Drop-off by Precinct <InfoTip termId="drop_off" />
                </h2>
                <p className="text-xs text-fg-muted mt-1">
                  Voted {data.base_year} {data.election_type} · Skipped{' '}
                  {data.compare_year} {data.election_type} · sorted by{' '}
                  {sortKey.replace(/_/g, ' ')} {sortDesc ? '↓' : '↑'}
                </p>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-fg-muted border-b border-border-base">
                    <SortHeader
                      label="Precinct"
                      active={sortKey === 'precinct_num'}
                      desc={sortDesc}
                      onClick={() => toggleSort('precinct_num')}
                    />
                    <SortHeader
                      label="Base"
                      active={sortKey === 'base_count'}
                      desc={sortDesc}
                      onClick={() => toggleSort('base_count')}
                      align="right"
                    />
                    <th className="py-2 px-3 text-right">Retained</th>
                    <SortHeader
                      label="Dropped"
                      active={sortKey === 'dropped_count'}
                      desc={sortDesc}
                      onClick={() => toggleSort('dropped_count')}
                      align="right"
                    />
                    <SortHeader
                      label="Drop-off %"
                      active={sortKey === 'drop_off_pct'}
                      desc={sortDesc}
                      onClick={() => toggleSort('drop_off_pct')}
                      align="right"
                    />
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map((p) => {
                    const rnk = rankWithinDrop(sortedRows, p.drop_off_pct);
                    return (
                    <tr
                      key={p.precinct_num}
                      className="border-b border-border-base hover:bg-bg-raised/60"
                    >
                      <td className="py-2 px-3">
                        <button
                          className="text-status-danger hover:text-accent-fg font-bold inline-flex items-center justify-center min-h-11 min-w-11 sm:min-h-0 sm:min-w-0"
                          onClick={() => onDrill?.('precinct', p.precinct_num)}
                          title={p.precinct_name}
                        >
                          P{p.precinct_num}
                        </button>
                        <div className="text-[10px] text-fg-muted">
                          {p.precinct_name}
                        </div>
                      </td>
                      <td className="py-2 px-3 text-right text-fg-secondary tabular-nums">
                        {p.base_count.toLocaleString()}
                      </td>
                      <td className="py-2 px-3 text-right text-fg-secondary tabular-nums">
                        {p.retained_count.toLocaleString()}
                      </td>
                      <td className="py-2 px-3 text-right text-fg-primary font-bold tabular-nums">
                        {p.dropped_count.toLocaleString()}
                      </td>
                      <td className="py-2 px-3 text-right tabular-nums min-w-[180px]">
                        <div className="flex items-center justify-end gap-1 mb-1 flex-wrap">
                          <DropPctBadge pct={p.drop_off_pct} />
                          <DeltaChip
                            value={p.drop_off_pct}
                            baseline={data.drop_off_pct}
                            label="vs county"
                            format="pct"
                            direction="lower-better"
                            compact
                          />
                          <RankBadge
                            rank={rnk}
                            total={sortedRows.length}
                            direction="lower-better"
                          />
                        </div>
                        <BarMeter
                          value={p.drop_off_pct}
                          baseline={data.drop_off_pct}
                          tierColor={false}
                          height={3}
                        />
                      </td>
                    </tr>
                    );
                  })}
                  {sortedRows.length === 0 && (
                    <tr>
                      <td
                        colSpan={5}
                        className="py-8 text-center text-fg-muted"
                      >
                        No precincts have a drop-off rate for this filter.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Optional voter ID sample */}
          {data.sample_dropped_voter_ids &&
            data.sample_dropped_voter_ids.length > 0 && (
              <div className="bg-bg-raised rounded-xl p-6 border border-border-base">
                <h3 className="text-sm font-bold text-fg-primary mb-2">
                  Sample dropped voter IDs (capped at 100)
                </h3>
                <p className="text-[11px] text-fg-muted mb-3">
                  This is a spot-check sample, not a bulk export. Full
                  per-voter export flows through the (still-TBD)
                  ExportEvent-logged path so it can be audited.
                </p>
                <div className="flex flex-wrap gap-1.5 text-[11px] font-mono text-fg-secondary">
                  {data.sample_dropped_voter_ids.map((id) => (
                    <span
                      key={id}
                      className="bg-bg-base border border-border-base rounded px-1.5 py-0.5"
                    >
                      {id}
                    </span>
                  ))}
                </div>
              </div>
            )}
        </>
      )}
    </div>
  );
}

// -------------------------------------------------------------------------
// Sub-components
// -------------------------------------------------------------------------

function Header({
  onBack,
  usingMock,
  onHelp,
}: {
  onBack: () => void;
  usingMock: boolean;
  onHelp?: () => void;
}) {
  return (
    <ViewHeader
      title="Drop-Off Detector"
      tagline="Voters who showed up in the base year but skipped the compare year — your reactivation list."
      tone="danger"
      inline={
        <>
          <LiveBadge mock={usingMock} />
          <InfoTip termId="drop_off" />
        </>
      }
      actions={onHelp ? <OnboardingHelpButton onClick={onHelp} /> : undefined}
    />
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-wide text-fg-muted font-semibold">
        {label}
      </span>
      {children}
    </label>
  );
}

function Stat({
  label,
  value,
  hint,
  accent = false,
}: {
  label: string;
  value: string;
  hint?: string;
  accent?: boolean;
}) {
  return (
    <div
      className={`rounded-lg border px-3 py-2 ${
        accent
          ? 'bg-status-danger-bg/30 border-status-danger/60'
          : 'bg-bg-raised border-border-base'
      }`}
    >
      <div className="text-[10px] uppercase tracking-wider text-fg-muted">
        {label}
      </div>
      <div className="mt-0.5 text-2xl font-bold text-fg-primary tabular-nums">
        {value}
      </div>
      {hint && <div className="text-[10px] text-fg-muted mt-0.5">{hint}</div>}
    </div>
  );
}

/** Rank a row within the list using "lower drop-off is better". 1 = best. */
function rankWithinDrop(rows: DropOffPrecinctRow[], target: number): number {
  let better = 0;
  for (const r of rows) {
    if (r.drop_off_pct < target) better += 1;
  }
  return better + 1;
}

function DropPctBadge({ pct }: { pct: number }) {
  let cls = 'bg-status-success-bg/20 text-status-success border-status-success/30';
  if (pct >= 30) cls = 'bg-status-danger-bg/30 text-status-danger border-status-danger/40';
  else if (pct >= 20) cls = 'bg-status-warning-bg/25 text-status-warning border-status-warning/40';
  else if (pct >= 10) cls = 'bg-status-warning-bg/15 text-status-warning border-status-warning/30';
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded border text-[11px] font-bold ${cls}`}
    >
      {pct.toFixed(1)}%
    </span>
  );
}

function SortHeader({
  label,
  active,
  desc,
  onClick,
  align = 'left',
}: {
  label: string;
  active: boolean;
  desc: boolean;
  onClick: () => void;
  align?: 'left' | 'right';
}) {
  return (
    <th
      className={`py-2 px-3 cursor-pointer select-none hover:text-fg-primary ${
        align === 'right' ? 'text-right' : ''
      } ${active ? 'text-status-danger' : ''}`}
      onClick={onClick}
    >
      {label} {active ? (desc ? '↓' : '↑') : ''}
    </th>
  );
}

export default DropOffView;
